import { type ReactElement, useId } from 'react';
import type { PhotoDimensions } from '@/entities/edit-area';
import type { CylinderPreviewImage } from '../model/use-cylinder-preview';

interface CylinderPreviewLayerProps {
  readonly image: CylinderPreviewImage;
  /** 저장되는 합성 설정의 multiply(결정 Q10). */
  readonly multiply: boolean;
  /** 그 면 마스크의 표시 URL(결정 R2). null 이면 자르지 않는다. */
  readonly maskUrl: string | null;
  /** 마스크 이미지를 놓을 사진 px 크기. 마스크는 그 면 사진과 같은 크기다(정의 교차 검사). */
  readonly photoSize: PhotoDimensions;
}

/**
 * 편집 SVG 안에서 사진 바로 위에 겹치는 원통 미리보기 이미지. 사진 px 배치에 늘려 그린다.
 * 포인터 입력은 받지 않아 표면·배치 드래그와 윤곽 추출을 가로막지 않는다.
 * multiply 는 사진 픽셀을 읽지 않고 CSS mix-blend-mode 로 곱하고, 마스크도 픽셀을 읽지 않고 SVG 알파 마스크로 자른다.
 */
export const CylinderPreviewLayer = ({
  image,
  multiply,
  maskUrl,
  photoSize,
}: CylinderPreviewLayerProps): ReactElement => {
  const maskId = useId();
  return (
    <>
      {maskUrl === null ? null : (
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          x={0}
          y={0}
          width={photoSize.widthPx}
          height={photoSize.heightPx}
          style={{ maskType: 'alpha' }}
        >
          <image
            href={maskUrl}
            x={0}
            y={0}
            width={photoSize.widthPx}
            height={photoSize.heightPx}
            preserveAspectRatio="none"
          />
        </mask>
      )}
      <image
        data-testid="cylinder-preview"
        aria-label="저장되지 않는 원통 미리보기"
        href={image.href}
        x={image.left}
        y={image.top}
        width={image.width}
        height={image.height}
        preserveAspectRatio="none"
        pointerEvents="none"
        mask={maskUrl === null ? undefined : `url(#${maskId})`}
        style={multiply ? { mixBlendMode: 'multiply' } : undefined}
      />
    </>
  );
};
